import React, { useEffect, useId, useState } from 'react';
import { Text } from '@lmn/design-system';
import { css } from 'styled-components';
import { useTranslation } from '@/app/components/translations/Translation';
import RoomMainAmenities from '@/app/components/hotel-detail/rooms/RoomMainAmenities';
import ImageWithFallback, { DEFAULT_FALLBACK_IMAGE } from '@/app/components/hotel-detail/ImageWithFallback';
import {
  getSingleSource,
  transformationMobileLayout,
} from '@/app/components/hotel-detail/getGallerySourceOptimization';
import { keyUpHandler } from '@/app/components/hotel-detail/common/common.helper';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { Icon } from '@/app/common/icons/Icon';
import { MealPlanOptionsGroups, RoomsIncludedInPrice } from './RoomPanelCommon';
import { useFetchHotelStatic } from '../hooks/useFetchHotelStatic';
import { RoomAvailabilityAlertLabel } from './RoomAvailabilityAlertLabel';
import { getMainAmenities, hasRoomDetails } from './roomCommon';
import { useRoomPanelOpenModal } from './RoomPanelModalProvider';
import { SubtleButton } from '../common/button';
import { RoomBedTypeSelection, shouldShowBedSelection } from './RoomBedTypeSelection';
import { RoomBedOptions } from './RoomBedOptions';
import { getRoomImageFallbackReason, RoomImageFallbackCaption } from './RoomImageFallbackCaption';
import { useLoadableImages } from '../hooks/useLoadableImages';

export type RoomPanelDesktopHeaderProps = {
  noRoomImages?: boolean;
  onSelectMealPlan?: (selectedMealPlan: MealPlanType) => void;
  room: RoomType;
  selectedMealPlan: MealPlanType;
} & React.HTMLAttributes<HTMLElement>;

export type DescriptionType = {
  type: string;
  text: string;
};
/*
 * Temporal fix to avoid showing room names with a dot at the end.
 * This should be fixed at supply level
 */
function removeDotsFromEndOfString(str: string) {
  let newStr = str.trim();
  while (newStr[newStr.length - 1] === '.') newStr = newStr.slice(0, -1);
  return newStr;
}

function RoomPanelDesktopHeader(props: RoomPanelDesktopHeaderProps) {
  const { room, noRoomImages, selectedMealPlan, onSelectMealPlan, ...rest } = props;

  const imageWidth = 280;
  const imageHeight = 136;

  const { t } = useTranslation();
  const { data: hotelStaticData } = useFetchHotelStatic();
  const { openModal } = useRoomPanelOpenModal();
  const { localization } = usePageWrapperData();
  const { imageBasePath } = localization;

  const hasImages = room.images.length > 0;
  // MPH-1321: the card shows the room's first photo that loads, each one that fails giving its place to the
  // next, and its counter counts only the photos left. The photos that failed are shared with the room
  // details gallery, so the card and the gallery show the same ones.
  const { loadableImages, onImageFallback } = useLoadableImages(room.images);
  const photosFailed = hasImages && loadableImages.length === 0;
  // This header shows the image block on a Top Secret stay too, so the caption needs its own guard.
  const captionReason = getRoomImageFallbackReason({
    hasImages,
    photosFailed,
    isTopSecret: !!hotelStaticData?.isTopSecret,
  });
  const captionId = useId();
  const [isDetailsModalOpen, setDetailsModalOpen] = useState<boolean>();

  const shownImage: string | undefined = loadableImages[0];
  const shownSrc = shownImage
    ? getSingleSource(shownImage, transformationMobileLayout)
    : `${imageBasePath}${DEFAULT_FALLBACK_IMAGE}`;

  const mainAmenities = getMainAmenities(t, room);
  const hasDetails = hasRoomDetails(room);

  useEffect(() => {
    if (isDetailsModalOpen == null) setDetailsModalOpen(false);
  }, [isDetailsModalOpen]);

  const onShowDetailsClick = () => {
    openModal(room);
  };

  if (room.configurations.length === 0) return null;
  const { roomsLeft } = room.configurations[room.selectedMealPlanIndex];

  const roomMainAmenitiesCss = css`
    display: flex;
    flex-wrap: wrap;
    column-gap: 16px;
    width: 100%;
  `;

  return (
    <div
      css={css`
        display: flex;
        background: ${(p) => p.theme.kallisto.color.background.static.backgroundLayer02};
        position: relative;
        border-radius: 8px;
        flex-direction: column;
        row-gap: 8px;
        width: ${imageWidth}px;
      `}
      {...rest}
    >
      {!noRoomImages ? (
        <div
          css={`
            width: ${imageWidth}px;
            height: ${imageHeight}px;
            position: relative;
            cursor: ${hasImages ? 'pointer' : 'default'};
          `}
          // MPH-1321: the image of a room without photos opens nothing, so it is no button; its caption is
          // read as plain text, and the "Show details" link below still opens the room details.
          {...(hasImages && {
            tabIndex: 0,
            role: 'button',
            onClick: onShowDetailsClick,
            onKeyUp: keyUpHandler(onShowDetailsClick),
            'aria-label': `${t('vacation.select.rooms.showdetails')} - ${room.name}`,
            'aria-describedby': captionReason ? captionId : undefined,
          })}
        >
          <ImageWithFallback
            key={shownSrc}
            url={[shownSrc]}
            alt=""
            loading="lazy"
            onFallback={shownImage ? () => onImageFallback(shownImage, shownSrc) : undefined}
            css={css`
              border-radius: 8px;
              width: ${imageWidth}px;
              height: ${imageHeight}px;
              object-fit: cover;
              -webkit-tap-highlight-color: transparent;
            `}
          />
          {captionReason && <RoomImageFallbackCaption id={captionId} reason={captionReason} />}
          {/* MPH-1321: counts only the photos left to show. */}
          {loadableImages.length > 1 ? (
            <div
              css={css`
                align-items: center;
                background: rgb(61 61 61 / 50%);
                border-radius: 4px;
                bottom: 8px;
                color: ${(p) => p.theme.kallisto.color.text.static.textOnDark};
                display: flex;
                font-size: ${(p) => p.theme.kallisto.font.size['150']};
                gap: 8px;
                height: 24px;
                padding: 0 8px;
                position: absolute;
                right: 8px;
              `}
            >
              <Icon category="UI Essentials" name="icon-photo-gallery" size={14} color="iconOnDark" />
              <span>{loadableImages.length}</span>
            </div>
          ) : null}
        </div>
      ) : hotelStaticData?.isTopSecret ? (
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 128px;
            background-color: ${(p) => p.theme.kallisto.color.common.special.topSecretHotels[200]};
            border-radius: 8px;
            padding: 8px;
          `}
        >
          <img
            css={`
              width: 36px;
              height: 36px;
            `}
            src={`${imageBasePath}logo-image-tsh.svg`}
            alt="tsh-icon-logo"
          />
        </div>
      ) : undefined}
      <div
        css={`
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          width: 100%;
        `}
      >
        <div
          css={`
            display: flex;
            flex-direction: column;
            row-gap: 8px;
            width: 100%;
          `}
        >
          <RoomTitle name={room.name} roomsIncludedInPrice={room.roomsIncludedInPrice} roomsLeft={roomsLeft} />
          {room.bedOptions && room.bedOptions.length ? (
            <>
              {/* {shouldShowRoomAvailabilityAlertLabel(roomsLeft) && <Separator />} */}
              {shouldShowBedSelection(selectedMealPlan.bedId, room.bedOptions) ? (
                <MealPlanOptionsGroups $hideBorder={false}>
                  <RoomBedTypeSelection
                    context="desktop-header"
                    room={room}
                    selectedMealPlan={selectedMealPlan}
                    onSelectMealPlan={onSelectMealPlan}
                  />
                </MealPlanOptionsGroups>
              ) : (
                <RoomBedOptions bedOptions={room.bedOptions} />
              )}
              {/* {(mainAmenities.length > 0 || hasDetails) && <Separator />} */}
            </>
          ) : undefined}

          {mainAmenities.length > 0 && (
            <RoomMainAmenities
              amenities={mainAmenities.slice(0, 4)}
              iconColor="iconSecondary"
              iconSize={16}
              color="textSecondary"
              css={roomMainAmenitiesCss}
            />
          )}

          {hasDetails && (
            <div>
              <SubtleButton
                onClick={onShowDetailsClick}
                data-testid="show-room-details"
                css={`
                  padding: 2px 0;
                `}
              >
                <Text variant="BodySmall">{t('vacation.select.rooms.showdetails')}</Text>
              </SubtleButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

type RoomTitleProps = {
  name: string;
  roomsIncludedInPrice: number;
  roomsLeft: number;
};

function RoomTitle(props: RoomTitleProps) {
  const { name, roomsIncludedInPrice, roomsLeft } = props;
  return (
    <>
      <div
        css={`
          display: flex;
          gap: 4px 8px;
          align-items: flex-start;
        `}
      >
        {roomsIncludedInPrice > 1 && <RoomsIncludedInPrice roomsIncludedInPrice={roomsIncludedInPrice} />}
        <Text variant="Title4">{removeDotsFromEndOfString(name)}</Text>
      </div>
      <RoomAvailabilityAlertLabel roomsLeft={roomsLeft} textVariant="BodySmall" />
    </>
  );
}

export default RoomPanelDesktopHeader;
